/**
 * Add-ons card — surfaces the four billable/opt-in add-ons in one place
 * so the firm-admin doesn't have to hunt for their toggles across the app.
 *
 * Each add-on has its own /api endpoint that mutates a bool flag on the
 * client record; the routes are already gated to firm_admin. This card
 * reads the flags directly off the client object passed in as a prop.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, Sunrise, Banknote, Gauge } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const ADDONS = [
    {
        key: "cash_register_enabled",
        endpoint: "/cash-register/clients/{id}/access",
        label: "Cash Register",
        icon: Wallet,
        hint: "Opening float, day-close variance and Excel export. Perfect for cafes / retail. Adds the Cash Register tab to the portal.",
    },
    {
        key: "morning_brief_enabled",
        endpoint: "/morning-brief/clients/{id}/access",
        label: "Morning Brief",
        icon: Sunrise,
        hint: "Today's 3 things that matter + Business Health Score + Daybook Coach. Shown at the top of the portal home.",
    },
    {
        key: "spend_insights_enabled",
        endpoint: "/spend-insights/clients/{id}/access",
        label: "Spend Insights",
        icon: Banknote,
        hint: "\"Where did my money go?\" — cash-out category breakdown tile + full /portal/cash-out page.",
    },
    {
        key: "job_status_odometer_enabled",
        endpoint: "/job-status/clients/{id}/access",
        label: "Job Status Odometer",
        icon: Gauge,
        hint: "Three-dial tile showing WIP · Waiting on you · Done recently. Sits under Morning Brief.",
    },
];

export default function AddOnsCard({ client, onChanged }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [busyKey, setBusyKey] = useState(null);
    const [local, setLocal] = useState({});

    useEffect(() => {
        if (!client) return;
        setLocal({
            cash_register_enabled: !!client.cash_register_enabled,
            morning_brief_enabled: !!client.morning_brief_enabled,
            spend_insights_enabled: !!client.spend_insights_enabled,
            job_status_odometer_enabled: !!client.job_status_odometer_enabled,
        });
    }, [client?.id, client?.cash_register_enabled, client?.morning_brief_enabled, client?.spend_insights_enabled, client?.job_status_odometer_enabled]);

    async function toggle(addon, next) {
        if (!isAdmin) { toast.error("Admin only"); return; }
        if (!client?.id) return;
        setBusyKey(addon.key);
        setLocal((s) => ({ ...s, [addon.key]: next }));
        try {
            await api.patch(addon.endpoint.replace("{id}", client.id), { enabled: next });
            toast.success(`${addon.label} ${next ? "enabled" : "disabled"}`);
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
            setLocal((s) => ({ ...s, [addon.key]: !next }));
        } finally {
            setBusyKey(null);
        }
    }

    return (
        <div className="co-card overflow-hidden" data-testid="portal-addons-card">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
                <h3 className="font-display text-lg font-medium">Add-ons</h3>
                <p className="mt-1 text-sm text-[#5E636E]">
                    Billable or opt-in extras. Off by default so books get sanity-checked first.
                </p>
            </div>
            <div className="px-6 py-4">
                <div className="rounded-lg border border-[#E4E4E7] divide-y divide-[#E4E4E7] overflow-hidden">
                    {ADDONS.map((a) => {
                        const on = !!local[a.key];
                        const busy = busyKey === a.key;
                        const Icon = a.icon;
                        return (
                            <div key={a.key} className="flex items-start justify-between gap-4 px-4 py-3" data-testid={`addon-row-${a.key}`}>
                                <div className="flex-1 min-w-0 flex items-start gap-3">
                                    <Icon className="h-4 w-4 text-[#0E3B2E] shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <Label htmlFor={`addon-${a.key}`} className="text-sm font-medium cursor-pointer">{a.label}</Label>
                                        <p className="mt-0.5 text-[11px] text-[#5E636E] leading-snug">{a.hint}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#5E636E]" />}
                                    <Switch
                                        id={`addon-${a.key}`}
                                        checked={on}
                                        onCheckedChange={(v) => toggle(a, v)}
                                        disabled={!isAdmin || busy}
                                        data-testid={`addon-toggle-${a.key}`}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
